import React, { useState, useEffect } from "react";
import axios from "axios";
import { Landmark, Plus, Trash2, CheckCircle2, X, Calculator, Info, ReceiptText } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const baseMonths = (() => {
  const opts = [];
  const now = new Date();
  for (let i = -3; i <= 2; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const kode = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    opts.push({ kode, label: d.toLocaleDateString("id-ID", { month: "long", year: "numeric" }) });
  }
  return opts;
})();
const monthOptions = [{ kode: "all", label: "Semua Bulan" }, ...baseMonths];

// Jenis pajak umum untuk usaha ISP di Indonesia
const TAX_TYPES = [
  { jenis: "PPh Final UMKM", label: "PPh Final UMKM — Omzet 0,5% (PP 20/2026)", tarif: 0.5, basis: "omzet", desc: "0,5% dari peredaran bruto (omzet). PP 20/2026 (pengganti PP 55/2022) — berlaku bagi WP omzet < Rp 4,8 M/tahun." },
  { jenis: "PPN", label: "PPN Keluaran — 11%", tarif: 11, basis: "manual", desc: "Pajak Pertambahan Nilai atas penyerahan Jasa Kena Pajak (bila sudah PKP)." },
  { jenis: "PPh 21", label: "PPh Pasal 21 — Gaji Karyawan", tarif: 0, basis: "gaji", desc: "Pajak penghasilan atas gaji karyawan (tarif progresif — isi nominal manual)." },
  { jenis: "PPh 23", label: "PPh Pasal 23 — Jasa 2%", tarif: 2, basis: "manual", desc: "Pemotongan 2% atas imbalan jasa teknik/manajemen." },
  { jenis: "PPh Badan", label: "PPh Badan — 22%", tarif: 22, basis: "manual", desc: "Pajak penghasilan badan atas laba fiskal tahunan." },
  { jenis: "Pajak Daerah", label: "Pajak Daerah / Reklame", tarif: 0, basis: "manual", desc: "Pajak reklame/spanduk & pajak daerah lain." },
  { jenis: "Lainnya", label: "Lainnya (manual)", tarif: 0, basis: "manual", desc: "Kewajiban pajak lain — isi nominal manual." },
];

const rp = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");
const todayStr = () => new Date().toISOString().split("T")[0];

export const KeuanganPajakPage = () => {
  const currentKode = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;
  const [list, setList] = useState([]);
  const [periode, setPeriode] = useState(currentKode);
  const [showAdd, setShowAdd] = useState(false);
  const [notif, setNotif] = useState("");
  const [omzetInfo, setOmzetInfo] = useState(null);
  const [formPeriode, setFormPeriode] = useState(currentKode);
  const [form, setForm] = useState({ tipeIdx: 0, judul: "", dasar_pengenaan: 0, tarif_persen: 0.5, keterangan: "" });
  // Setor / bukti bayar
  const [showSetor, setShowSetor] = useState(false);
  const [setorTarget, setSetorTarget] = useState(null);
  const [setorForm, setSetorForm] = useState({ tanggal_bayar: todayStr(), metode_bayar: "Transfer Bank", no_referensi: "", bukti_url: "" });

  const effectivePeriode = periode === "all" ? currentKode : periode;
  const activeType = TAX_TYPES[form.tipeIdx];

  const fetchData = async () => {
    const url = periode === "all" ? `${BACKEND_URL}/api/pajak` : `${BACKEND_URL}/api/pajak?periode=${periode}`;
    const res = await axios.get(url, { withCredentials: true });
    setList(res.data);
  };
  const fetchOmzet = async (p) => {
    const res = await axios.get(`${BACKEND_URL}/api/pajak/omzet?periode=${p}`, { withCredentials: true });
    setOmzetInfo(res.data);
    return res.data;
  };

  useEffect(() => { fetchData(); fetchOmzet(effectivePeriode); }, [periode]);
  const notify = (m) => { setNotif(m); setTimeout(() => setNotif(""), 4000); };

  const computedNominal = (Number(form.dasar_pengenaan) || 0) * (Number(form.tarif_persen) || 0) / 100;

  const dppForType = (type, info) => {
    if (type.basis === "omzet") return info?.omzet || 0;
    if (type.basis === "gaji") return info?.total_gaji || 0;
    return 0;
  };

  const openForm = async () => {
    const p = effectivePeriode;
    setFormPeriode(p);
    const info = await fetchOmzet(p);
    const t = TAX_TYPES[0];
    setForm({ tipeIdx: 0, judul: "", dasar_pengenaan: dppForType(t, info), tarif_persen: t.tarif, keterangan: "" });
    setShowAdd(true);
  };

  const onSelectType = (idx) => {
    const t = TAX_TYPES[idx];
    const dpp = t.basis === "manual" ? (Number(form.dasar_pengenaan) || 0) : dppForType(t, omzetInfo);
    setForm({ ...form, tipeIdx: idx, tarif_persen: t.tarif, dasar_pengenaan: dpp });
  };

  const onChangeFormPeriode = async (p) => {
    setFormPeriode(p);
    const info = await fetchOmzet(p);
    const t = TAX_TYPES[form.tipeIdx];
    if (t.basis !== "manual") setForm((f) => ({ ...f, dasar_pengenaan: dppForType(t, info) }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const periodeLabel = baseMonths.find((o) => o.kode === formPeriode)?.label || formPeriode;
    const judul = form.judul.trim() || `${activeType.jenis} — ${periodeLabel}`;
    await axios.post(`${BACKEND_URL}/api/pajak`, {
      judul, jenis: activeType.jenis, periode: formPeriode,
      dasar_pengenaan: Number(form.dasar_pengenaan) || 0,
      tarif_persen: Number(form.tarif_persen) || 0,
      nominal: computedNominal, keterangan: form.keterangan,
    }, { withCredentials: true });
    setShowAdd(false);
    fetchData();
    notify("Pajak berhasil dihitung & dicatat!");
  };

  const openSetor = (item) => {
    setSetorTarget(item);
    setSetorForm({ tanggal_bayar: todayStr(), metode_bayar: "Transfer Bank", no_referensi: "", bukti_url: "" });
    setShowSetor(true);
  };
  const handleSetorSubmit = async (e) => {
    e.preventDefault();
    await axios.put(`${BACKEND_URL}/api/pajak/${setorTarget.id}/setor`, setorForm, { withCredentials: true });
    setShowSetor(false);
    fetchData();
    notify("Pajak berhasil disetor & bukti bayar tersimpan.");
  };
  const handleDelete = async (id) => {
    if (!window.confirm("Hapus data pajak ini?")) return;
    await axios.delete(`${BACKEND_URL}/api/pajak/${id}`, { withCredentials: true });
    fetchData();
  };

  const totalPajak = list.reduce((a, i) => a + (i.nominal || 0), 0);
  const bebanPajak = list.filter((i) => i.status === "SUDAH_SETOR").reduce((a, i) => a + (i.nominal || 0), 0);
  const belumSetor = totalPajak - bebanPajak;

  return (
    <div className="space-y-6" data-testid="pajak-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl">
        <div>
          <span className="px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 text-[10px] font-bold uppercase tracking-wider">Tax Management</span>
          <h1 className="text-xl sm:text-2xl font-black text-white mt-1">Manajemen Pajak</h1>
          <p className="text-xs text-slate-400 mt-0.5">Kalkulator pajak otomatis (PPh Final Omzet 0,5% PP 20/2026, PPN, PPh 21/23, dll). Beban pajak dihitung setelah disetor.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={periode} onChange={(e) => setPeriode(e.target.value)} data-testid="pajak-periode" className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-semibold">
            {monthOptions.map((o) => <option key={o.kode} value={o.kode}>{o.label}</option>)}
          </select>
          <button onClick={openForm} data-testid="add-pajak-btn" className="px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-xs font-bold rounded-xl shadow-lg shadow-cyan-600/30 flex items-center gap-2">
            <Calculator className="w-4 h-4" /> Hitung Pajak
          </button>
        </div>
      </div>

      {notif && <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 rounded-xl text-xs flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> {notif}</div>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800"><p className="text-[11px] text-slate-400">Omzet {baseMonths.find((o) => o.kode === effectivePeriode)?.label?.split(" ")[0] || ""}</p><p className="text-lg font-black text-emerald-400 mt-1 font-mono">{rp(omzetInfo?.omzet)}</p><p className="text-[10px] text-slate-500">tagihan + pendapatan lain</p></div>
        <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800"><p className="text-[11px] text-slate-400">Total Pajak {periode === "all" ? "(semua)" : ""}</p><p className="text-lg font-black text-cyan-400 mt-1 font-mono">{rp(totalPajak)}</p></div>
        <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800"><p className="text-[11px] text-slate-400">Beban Pajak (Disetor)</p><p className="text-lg font-black text-rose-400 mt-1 font-mono">{rp(bebanPajak)}</p></div>
        <div className="p-4 rounded-2xl bg-amber-950/20 border border-amber-500/30"><p className="text-[11px] text-slate-400">Belum Disetor (belum jadi beban)</p><p className="text-lg font-black text-amber-400 mt-1 font-mono">{rp(belumSetor)}</p></div>
      </div>

      <div className="p-3 bg-slate-900/60 border border-slate-800 rounded-xl text-[11px] text-slate-400 flex items-start gap-2">
        <Info className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
        <span>Pajak yang <b className="text-amber-300">belum disetor</b> tidak dihitung sebagai beban di Laporan Laba Rugi (beban = 0). Setelah ditekan <b className="text-emerald-300">Bayar</b> & bukti diinput, pajak baru mengurangi laba bersih.</span>
      </div>

      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead><tr className="border-b border-slate-800 text-slate-400 font-semibold">
            <th className="py-2.5 px-3">Keterangan</th><th className="py-2.5 px-3">Jenis</th><th className="py-2.5 px-3">Periode</th><th className="py-2.5 px-3">DPP × Tarif</th><th className="py-2.5 px-3">Nominal</th><th className="py-2.5 px-3">Status / Bukti</th><th className="py-2.5 px-3 text-right">Aksi</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-800/60">
            {list.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-500">Belum ada data pajak. Klik "Hitung Pajak".</td></tr>}
            {list.map((i) => (
              <tr key={i.id} className="hover:bg-slate-900/40" data-testid={`pajak-row-${i.id}`}>
                <td className="py-3 px-3 font-bold text-white">{i.judul}</td>
                <td className="py-3 px-3"><span className="px-2 py-0.5 rounded bg-cyan-500/15 text-cyan-300 text-[11px]">{i.jenis}</span></td>
                <td className="py-3 px-3 text-slate-300">{i.periode}</td>
                <td className="py-3 px-3 text-slate-400 font-mono">{rp(i.dasar_pengenaan)} × {i.tarif_persen}%</td>
                <td className="py-3 px-3 font-mono font-bold text-cyan-400">{rp(i.nominal)}</td>
                <td className="py-3 px-3">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${i.status === "SUDAH_SETOR" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>{i.status === "SUDAH_SETOR" ? "SUDAH SETOR" : "BELUM SETOR"}</span>
                  {i.status === "SUDAH_SETOR" && (
                    <div className="text-[10px] text-slate-500 mt-1">
                      {i.tanggal_bayar || i.tanggal} · {i.metode_bayar || "-"}{i.no_referensi ? ` · Ref: ${i.no_referensi}` : ""}
                      {i.bukti_url ? <a href={i.bukti_url} target="_blank" rel="noreferrer" className="text-cyan-400 underline ml-1">bukti</a> : ""}
                    </div>
                  )}
                </td>
                <td className="py-3 px-3 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    {i.status !== "SUDAH_SETOR" && <button onClick={() => openSetor(i)} data-testid={`setor-pajak-${i.id}`} className="px-2 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-[11px] font-bold">Bayar</button>}
                    <button onClick={() => handleDelete(i.id)} className="p-1.5 text-rose-400 hover:bg-rose-950/60 rounded-lg"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal Kalkulator */}
      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0f172a] border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl p-5 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <h3 className="text-sm font-bold text-white flex items-center gap-2"><Calculator className="w-4 h-4 text-cyan-400" /> Kalkulator Pajak</h3>
              <button onClick={() => setShowAdd(false)} className="text-slate-400"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={handleSave} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-300 mb-1">Periode</label>
                  <select value={formPeriode} onChange={(e) => onChangeFormPeriode(e.target.value)} data-testid="pajak-form-periode" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100">
                    {baseMonths.map((o) => <option key={o.kode} value={o.kode}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-300 mb-1">Jenis Pajak</label>
                  <select value={form.tipeIdx} onChange={(e) => onSelectType(Number(e.target.value))} data-testid="pajak-jenis-select" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100">
                    {TAX_TYPES.map((t, idx) => <option key={idx} value={idx}>{t.jenis}</option>)}
                  </select>
                </div>
              </div>
              <p className="text-[10px] text-slate-500 -mt-1">{activeType.desc}</p>

              {activeType.basis === "omzet" && (
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-[11px] text-emerald-300 flex items-center justify-between">
                  <span>Omzet periode ini (otomatis)</span>
                  <span className="font-mono font-bold">{rp(omzetInfo?.omzet)}</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-300 mb-1">Dasar Pengenaan (DPP)</label>
                  <input type="number" value={form.dasar_pengenaan} onChange={(e) => setForm({ ...form, dasar_pengenaan: e.target.value })} data-testid="pajak-dpp-input" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100 font-mono" />
                </div>
                <div>
                  <label className="block text-xs text-slate-300 mb-1">Tarif (%)</label>
                  <input type="number" step="0.1" value={form.tarif_persen} onChange={(e) => setForm({ ...form, tarif_persen: e.target.value })} data-testid="pajak-tarif-input" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100 font-mono" />
                </div>
              </div>

              <div className="p-3 bg-slate-950 rounded-xl space-y-1.5">
                <div className="flex justify-between text-[11px] text-slate-400"><span>{rp(form.dasar_pengenaan)} × {form.tarif_persen}%</span><span>=</span></div>
                <div className="flex justify-between items-center"><span className="text-xs text-slate-300 font-semibold">Pajak Terutang</span><span className="font-mono font-black text-cyan-400 text-lg" data-testid="pajak-hasil">{rp(computedNominal)}</span></div>
              </div>

              <div><label className="block text-xs text-slate-300 mb-1">Keterangan (opsional)</label><input type="text" value={form.keterangan} onChange={(e) => setForm({ ...form, keterangan: e.target.value })} placeholder="mis. Masa pajak Juni 2026" data-testid="pajak-judul-input" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100" /></div>

              <div className="pt-1 flex justify-end gap-2">
                <button type="button" onClick={() => setShowAdd(false)} className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded-xl text-xs">Batal</button>
                <button type="submit" data-testid="save-pajak-btn" className="px-4 py-1.5 bg-cyan-600 text-white rounded-xl text-xs font-bold">Simpan Perhitungan</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Setor / Bukti Bayar */}
      {showSetor && setorTarget && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0f172a] border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl p-5 space-y-4">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <h3 className="text-sm font-bold text-white flex items-center gap-2"><ReceiptText className="w-4 h-4 text-emerald-400" /> Setor Pajak & Bukti Bayar</h3>
              <button onClick={() => setShowSetor(false)} className="text-slate-400"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-2.5 bg-slate-950 rounded-xl text-xs flex justify-between"><span className="text-slate-400">{setorTarget.judul}</span><span className="font-mono font-bold text-cyan-400">{rp(setorTarget.nominal)}</span></div>
            <form onSubmit={handleSetorSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs text-slate-300 mb-1">Tanggal Bayar *</label><input required type="date" value={setorForm.tanggal_bayar} onChange={(e) => setSetorForm({ ...setorForm, tanggal_bayar: e.target.value })} data-testid="setor-tanggal-input" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100" /></div>
                <div><label className="block text-xs text-slate-300 mb-1">Metode Bayar</label>
                  <select value={setorForm.metode_bayar} onChange={(e) => setSetorForm({ ...setorForm, metode_bayar: e.target.value })} data-testid="setor-metode-select" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100">
                    <option>Transfer Bank</option><option>e-Billing DJP</option><option>QRIS</option><option>Tunai</option>
                  </select>
                </div>
              </div>
              <div><label className="block text-xs text-slate-300 mb-1">No. Referensi / NTPN</label><input type="text" value={setorForm.no_referensi} onChange={(e) => setSetorForm({ ...setorForm, no_referensi: e.target.value })} placeholder="mis. NTPN 0123456789ABCDEF" data-testid="setor-referensi-input" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100" /></div>
              <div><label className="block text-xs text-slate-300 mb-1">Link/URL Bukti Bayar (opsional)</label><input type="text" value={setorForm.bukti_url} onChange={(e) => setSetorForm({ ...setorForm, bukti_url: e.target.value })} placeholder="https://..." data-testid="setor-bukti-input" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100" /></div>
              <div className="pt-1 flex justify-end gap-2">
                <button type="button" onClick={() => setShowSetor(false)} className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded-xl text-xs">Batal</button>
                <button type="submit" data-testid="submit-setor-btn" className="px-4 py-1.5 bg-emerald-600 text-white rounded-xl text-xs font-bold">Simpan & Setor</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
